// AdminConsoleScene.js
import { checkGameExists, listenToGameUpdates, startGame, joinGame } from "../services/multiplayerService.js";
import firebase, { auth, db } from "../firebaseConfig.js";
import { realtimeDB } from "../firebaseConfig.js";

class AdminConsoleScene extends Phaser.Scene {
  constructor() {
    super({ key: "AdminConsoleScene" });
    this.activeGames = [];
    this.selectedGameCode = null;
    this.gameListener = null;
    this.devMode = false;
    this.debugLog = [];
  }
  
  create() {
    // Background
    this.add.rectangle(0, 0, this.cameras.main.width, this.cameras.main.height, 0x000044).setOrigin(0);
    
    // Title
    this.add.text(this.cameras.main.width / 2, 50, "ADMIN CONSOLE", {
      fontFamily: 'Arial',
      fontSize: '48px',
      color: '#FF00FF',
      align: 'center'
    }).setOrigin(0.5);
    
    // Create UI sections
    this.createActiveSessions();
    this.createGameControls();
    this.createDevTools();
    this.createDebugLog();
    this.createActionButtons();
    
    // Fetch active games right away
    this.refreshGamesList();
    
    // Auto-refresh every 10 seconds
    this.time.addEvent({
      delay: 10000,
      callback: this.refreshGamesList,
      callbackScope: this,
      loop: true
    });
  }
  
  createActiveSessions() {
    // Active sessions title
    this.add.text(20, 120, "ACTIVE SESSIONS", {
      fontFamily: 'Arial',
      fontSize: '24px',
      color: '#00FFFF',
      align: 'left'
    });
    
    // Create games list container
    this.gamesListBackground = this.add.rectangle(20, 160, 400, 300, 0x333344).setOrigin(0);
    
    // Games list (initially empty)
    this.gamesList = this.add.text(30, 170, "Loading games...", {
      fontFamily: 'Arial',
      fontSize: '16px',
      color: '#FFFFFF',
      align: 'left'
    });
    
    // Refresh button
    const refreshBtn = this.add.rectangle(380, 130, 100, 30, 0x0099FF, 0.8);
    this.add.text(380, 130, "REFRESH", {
      fontFamily: 'Arial',
      fontSize: '14px',
      color: '#FFFFFF'
    }).setOrigin(0.5);
    
    refreshBtn.setInteractive({ useHandCursor: true });
    refreshBtn.on('pointerdown', () => {
      this.refreshGamesList();
    });
  }
  
  createGameControls() {
    // Game controls section
    this.add.text(450, 120, "GAME CONTROLS", {
      fontFamily: 'Arial',
      fontSize: '24px',
      color: '#00FFFF',
      align: 'left'
    });
    
    this.controlsBackground = this.add.rectangle(450, 160, 400, 300, 0x333344).setOrigin(0);
    
    // Selected game info
    this.selectedGameText = this.add.text(460, 170, "No game selected", {
      fontFamily: 'Arial',
      fontSize: '16px',
      color: '#FFFFFF'
    });
    
    // Game details
    this.gameDetailsText = this.add.text(460, 200, "", {
      fontFamily: 'Arial',
      fontSize: '14px',
      color: '#AAAAAA'
    });
    
    // Players list
    this.playersListText = this.add.text(460, 270, "", {
      fontFamily: 'Arial',
      fontSize: '14px',
      color: '#AAAAAA'
    });
    
    // Control buttons
    // Start game button
    const startGameBtn = this.add.rectangle(500, 410, 120, 40, 0x009900, 0.8);
    this.add.text(500, 410, "START GAME", {
      fontFamily: 'Arial',
      fontSize: '14px',
      color: '#FFFFFF'
    }).setOrigin(0.5);
    
    startGameBtn.setInteractive({ useHandCursor: true });
    startGameBtn.on('pointerdown', () => {
      this.startSelectedGame();
    });
    
    // Join game button
    const joinGameBtn = this.add.rectangle(650, 410, 120, 40, 0x0099FF, 0.8);
    this.add.text(650, 410, "JOIN GAME", {
      fontFamily: 'Arial',
      fontSize: '14px',
      color: '#FFFFFF'
    }).setOrigin(0.5);
    
    joinGameBtn.setInteractive({ useHandCursor: true });
    joinGameBtn.on('pointerdown', () => {
      this.joinSelectedGame();
    });
    
    // Force start with single player
    const forceSingleBtn = this.add.rectangle(800, 410, 180, 40, 0xFF9900, 0.8);
    this.add.text(800, 410, "START SINGLE PLAYER", {
      fontFamily: 'Arial',
      fontSize: '12px',
      color: '#FFFFFF'
    }).setOrigin(0.5);
    
    forceSingleBtn.setInteractive({ useHandCursor: true });
    forceSingleBtn.on('pointerdown', () => {
      this.forceSinglePlayerStart();
    });
  }
  
  createDevTools() {
    // Dev tools section
    this.add.text(880, 120, "DEV TOOLS", {
      fontFamily: 'Arial',
      fontSize: '24px',
      color: '#00FFFF',
      align: 'left'
    });
    
    this.devToolsBackground = this.add.rectangle(880, 160, 400, 300, 0x333344).setOrigin(0);
    
    // Dev mode toggle
    const devModeBtn = this.add.rectangle(980, 180, 160, 40, 0x777777, 0.8);
    this.devModeText = this.add.text(980, 180, "DEV MODE: OFF", {
      fontFamily: 'Arial',
      fontSize: '16px',
      color: '#FFFFFF'
    }).setOrigin(0.5);
    
    devModeBtn.setInteractive({ useHandCursor: true });
    devModeBtn.on('pointerdown', () => {
      this.devMode = !this.devMode;
      this.devModeText.setText(`DEV MODE: ${this.devMode ? 'ON' : 'OFF'}`);
      devModeBtn.fillColor = this.devMode ? 0x00AA00 : 0x777777;
      this.log(`Dev mode ${this.devMode ? 'enabled' : 'disabled'}`);
    });
    
    // Quick test game button
    const quickTestBtn = this.add.rectangle(980, 230, 160, 40, 0xFF00FF, 0.8);
    this.add.text(980, 230, "QUICK TEST GAME", {
      fontFamily: 'Arial',
      fontSize: '14px',
      color: '#FFFFFF'
    }).setOrigin(0.5);
    
    quickTestBtn.setInteractive({ useHandCursor: true });
    quickTestBtn.on('pointerdown', () => {
      this.createAndStartTestGame();
    });
    
    // Speed controls
    this.add.text(900, 280, "GAME SPEED:", {
      fontFamily: 'Arial',
      fontSize: '14px',
      color: '#FFFFFF'
    });
    
    // Normal speed
    const normalSpeedBtn = this.add.rectangle(930, 310, 80, 30, 0x009900, 0.8);
    this.add.text(930, 310, "NORMAL", {
      fontFamily: 'Arial',
      fontSize: '12px',
      color: '#FFFFFF'
    }).setOrigin(0.5);
    
    normalSpeedBtn.setInteractive({ useHandCursor: true });
    normalSpeedBtn.on('pointerdown', () => {
      this.log("Game speed set to normal");
      localStorage.setItem('gameSpeed', '1');
    });
    
    // Fast speed
    const fastSpeedBtn = this.add.rectangle(1020, 310, 80, 30, 0xFFAA00, 0.8);
    this.add.text(1020, 310, "FAST", {
      fontFamily: 'Arial',
      fontSize: '12px',
      color: '#FFFFFF'
    }).setOrigin(0.5);
    
    fastSpeedBtn.setInteractive({ useHandCursor: true });
    fastSpeedBtn.on('pointerdown', () => {
      this.log("Game speed set to fast (2x)");
      localStorage.setItem('gameSpeed', '2');
    });
    
    // Super fast speed
    const superFastBtn = this.add.rectangle(1110, 310, 80, 30, 0xFF0000, 0.8);
    this.add.text(1110, 310, "SUPER", {
      fontFamily: 'Arial',
      fontSize: '12px',
      color: '#FFFFFF'
    }).setOrigin(0.5);
    
    superFastBtn.setInteractive({ useHandCursor: true });
    superFastBtn.on('pointerdown', () => {
      this.log("Game speed set to super fast (4x)");
      localStorage.setItem('gameSpeed', '4');
    });
    
    // God mode toggle
    const godModeBtn = this.add.rectangle(980, 350, 160, 40, 0x777777, 0.8);
    this.godModeText = this.add.text(980, 350, "GOD MODE: OFF", {
      fontFamily: 'Arial',
      fontSize: '16px',
      color: '#FFFFFF'
    }).setOrigin(0.5);
    
    godModeBtn.setInteractive({ useHandCursor: true });
    godModeBtn.on('pointerdown', () => {
      const godMode = localStorage.getItem('godMode') === 'true' ? false : true;
      localStorage.setItem('godMode', godMode);
      this.godModeText.setText(`GOD MODE: ${godMode ? 'ON' : 'OFF'}`);
      godModeBtn.fillColor = godMode ? 0xFF0000 : 0x777777;
      this.log(`God mode ${godMode ? 'enabled' : 'disabled'}`);
    });
    
    // Initialize god mode display from localStorage
    const godMode = localStorage.getItem('godMode') === 'true';
    this.godModeText.setText(`GOD MODE: ${godMode ? 'ON' : 'OFF'}`);
    godModeBtn.fillColor = godMode ? 0xFF0000 : 0x777777;
  }
  
  createDebugLog() {
    // Debug log section
    this.add.text(20, 480, "DEBUG LOG", {
      fontFamily: 'Arial',
      fontSize: '24px',
      color: '#00FFFF',
      align: 'left'
    });
    
    // Create log container
    this.logBackground = this.add.rectangle(20, 520, 1260, 200, 0x333344).setOrigin(0);
    
    // Log text
    this.logText = this.add.text(30, 530, "", {
      fontFamily: 'Arial',
      fontSize: '14px',
      color: '#AAAAAA',
      align: 'left'
    });
    
    // Clear log button
    const clearLogBtn = this.add.rectangle(300, 490, 100, 30, 0xAA0000, 0.8);
    this.add.text(300, 490, "CLEAR LOG", {
      fontFamily: 'Arial',
      fontSize: '14px',
      color: '#FFFFFF'
    }).setOrigin(0.5);
    
    clearLogBtn.setInteractive({ useHandCursor: true });
    clearLogBtn.on('pointerdown', () => {
      this.debugLog = [];
      this.updateLogDisplay();
    });
    
    // Add first log
    this.log("Admin console initialized");
  }
  
  createActionButtons() {
    // Back button
    const backBtn = this.add.rectangle(this.cameras.main.width / 2, 740, 200, 50, 0x666666, 0.8);
    this.add.text(this.cameras.main.width / 2, 740, "BACK TO MENU", {
      fontFamily: 'Arial',
      fontSize: '18px',
      color: '#FFFFFF'
    }).setOrigin(0.5);
    
    backBtn.setInteractive({ useHandCursor: true });
    backBtn.on('pointerdown', () => {
      // Clean up listeners
      this.cleanupListeners();
      
      // Go back to menu
      sceneManager.switchToScene("MultiplayerMenuScene");
    });
  }
  
  async refreshGamesList() {
    try {
      this.gamesList.setText("Loading games...");
      
      // Get active games
      const games = await getActiveGames();
      this.activeGames = games || [];
      
      if (!this.activeGames || this.activeGames.length === 0) {
        this.gamesList.setText("No active games found");
        return;
      }
      
      // Update games list text
      let gamesText = "";
      this.activeGames.forEach((game, index) => {
        const selected = game.code === this.selectedGameCode ? "→ " : "";
        gamesText += `${selected}${index + 1}. ${game.code} - Host: ${game.hostName || 'Unknown'}\n   Players: ${game.playerCount || 0}/${game.maxPlayers || 4}, Mode: ${game.gameMode || 'Unknown'}\n\n`;
      });
      
      this.gamesList.setText(gamesText);
      
      // Make game codes clickable
      this.activeGames.forEach((game, index) => {
        const y = 170 + index * 48; // Adjust based on your text positioning
        const clickArea = this.add.rectangle(220, y, 400, 20, 0xFFFFFF, 0.001);
        clickArea.setOrigin(0.5, 0);
        clickArea.setInteractive({ useHandCursor: true });
        clickArea.on('pointerdown', () => {
          this.selectGame(game.code);
        });
      });
      
      this.log(`Refreshed games list: ${this.activeGames.length} active games`);
    } catch (error) {
      console.error("Error refreshing games list:", error);
      this.log(`Error refreshing games: ${error.message}`);
      this.gamesList.setText("Error loading games");
    }
  }
  
  selectGame(gameCode) {
    // Deselect if already selected
    if (this.selectedGameCode === gameCode) {
      this.selectedGameCode = null;
      this.selectedGameText.setText("No game selected");
      this.gameDetailsText.setText("");
      this.playersListText.setText("");
      
      // Clean up game listener
      if (this.gameListener) {
        this.gameListener();
        this.gameListener = null;
      }
      
      // Refresh games list to remove selection arrow
      this.refreshGamesList();
      return;
    }
    
    // Set selected game
    this.selectedGameCode = gameCode;
    this.selectedGameText.setText(`Selected Game: ${gameCode}`);
    
    // Set up listener for the selected game
    if (this.gameListener) {
      this.gameListener();
    }
    
    this.gameListener = listenToGameUpdates(gameCode, (gameData) => {
      if (!gameData) {
        this.log(`Game ${gameCode} no longer exists`);
        this.selectedGameCode = null;
        this.selectedGameText.setText("Game no longer exists");
        this.gameDetailsText.setText("");
        this.playersListText.setText("");
        this.refreshGamesList();
        return;
      }
      
      // Update game details
      this.gameDetailsText.setText(
        `Host: ${gameData.hostName || 'Unknown'}\n` +
        `Status: ${gameData.status || 'Unknown'}\n` +
        `Game Mode: ${gameData.gameMode || 'Unknown'}\n` +
        `Time Limit: ${gameData.timeLimit || 0} seconds\n` +
        `Created: ${new Date(gameData.createdAt || Date.now()).toLocaleTimeString()}`
      );
      
      // Update players list
      let playersText = "Players:\n";
      if (gameData.players) {
        Object.entries(gameData.players).forEach(([playerId, player]) => {
          playersText += `- ${player.name || 'Unknown'} (${player.isHost ? 'Host' : 'Player'})\n`;
          if (this.devMode) {
            // Add more details in dev mode
            playersText += `  Score: ${player.score || 0}, Lives: ${player.lives || 0}\n`;
          }
        });
      }
      
      this.playersListText.setText(playersText);
    });
    
    // Refresh games list to show selection arrow
    this.refreshGamesList();
    this.log(`Selected game ${gameCode}`);
  }
  
  async startSelectedGame() {
    if (!this.selectedGameCode) {
      this.log("No game selected");
      return;
    }
    
    try {
      // Get current user
      const user = auth.currentUser;
      if (!user) {
        this.log("Must be logged in to start a game");
        return;
      }
      
      this.log(`Starting game ${this.selectedGameCode}...`);
      
      // Start the game
      await startGame(this.selectedGameCode, user.uid);
      
      this.log(`Game ${this.selectedGameCode} started successfully`);
    } catch (error) {
      console.error("Error starting game:", error);
      this.log(`Error starting game: ${error.message}`);
    }
  }
  
  async joinSelectedGame() {
    if (!this.selectedGameCode) {
      this.log("No game selected");
      return;
    }
    
    try {
      // Get current user
      const user = auth.currentUser;
      if (!user) {
        this.log("Must be logged in to join a game");
        return;
      }
      
      this.log(`Joining game ${this.selectedGameCode}...`);
      
      // Join the game
      await joinGame(this.selectedGameCode, user.uid, user.displayName || "Admin");
      
      this.log(`Joined game ${this.selectedGameCode} successfully`);
      
      // Start the multiplayer game scene
      this.cleanupListeners();
      sceneManager.switchToScene("MultiplayerGameScene", { 
        gameCode: this.selectedGameCode, 
        isHost: false,
        isAdmin: true
      });
    } catch (error) {
      console.error("Error joining game:", error);
      this.log(`Error joining game: ${error.message}`);
    }
  }
  
  async forceSinglePlayerStart() {
    if (!this.selectedGameCode) {
      this.log("No game selected");
      return;
    }
    
    try {
      // Get current user
      const user = auth.currentUser;
      if (!user) {
        this.log("Must be logged in to start a game");
        return;
      }
      
      this.log(`Force starting game ${this.selectedGameCode} with single player...`);
      
      // Start the game even with only one player
      await startGame(this.selectedGameCode, user.uid, true); // true for force start
      
      this.log(`Game ${this.selectedGameCode} force started successfully`);
    } catch (error) {
      console.error("Error force starting game:", error);
      this.log(`Error force starting game: ${error.message}`);
    }
  }
  
  async createAndStartTestGame() {
    try {
      // Get current user
      const user = auth.currentUser;
      if (!user) {
        this.log("Must be logged in to create a test game");
        return;
      }
      
      this.log("Creating test game...");
      
      // Import the function we need
      const { createGameSession } = await import("../services/multiplayerService.js");
      
      // Create a new game session
      const gameCode = await createGameSession(
        user.uid, 
        "Admin Test", 
        "timeAttack", 
        600 // 10 minute time limit
      );
      
      this.log(`Test game created with code: ${gameCode}`);
      
      // Auto-select the new game
      this.selectGame(gameCode);
      
      // Wait a moment then force start
      this.time.delayedCall(500, async () => {
        try {
          // Force start the game with single player
          await startGame(gameCode, user.uid, true); // true for force start
          
          this.log(`Test game ${gameCode} started in single player mode`);
          
          // Start the game scene
          this.cleanupListeners();
          sceneManager.switchToScene("MultiplayerGameScene", { 
            gameCode: gameCode, 
            isHost: true,
            isAdmin: true
          });
        } catch (error) {
          console.error("Error starting test game:", error);
          this.log(`Error starting test game: ${error.message}`);
        }
      });
    } catch (error) {
      console.error("Error creating test game:", error);
      this.log(`Error creating test game: ${error.message}`);
    }
  }
  
  log(message) {
    // Add timestamp
    const now = new Date();
    const timestamp = now.toLocaleTimeString();
    const logMessage = `[${timestamp}] ${message}`;
    
    // Add to debug log array
    this.debugLog.push(logMessage);
    
    // Keep only the last 10 entries
    if (this.debugLog.length > 10) {
      this.debugLog.shift();
    }
    
    // Update log display
    this.updateLogDisplay();
    
    // Print to console too
    console.log(message);
  }
  
  updateLogDisplay() {
    this.logText.setText(this.debugLog.join('\n'));
  }
  
  cleanupListeners() {
    // Clean up game listener
    if (this.gameListener) {
      this.gameListener();
      this.gameListener = null;
    }
  }
  
  shutdown() {
    this.cleanupListeners();
  }
}

// Missing function implementation
async function getActiveGames() {
  try {
    // Check if Firebase is available
    if (!db) {
      console.error("Firebase db is not available");
      return [];
    }
    
    // Get active games from Firestore
    const gamesQuery = await db.collection("games")
      .where("status", "==", "waiting")
      .orderBy("createdAt", "desc")
      .limit(20)
      .get();
    
    const games = [];
    
    gamesQuery.forEach((doc) => {
      const data = doc.data();
      games.push({
        code: doc.id,
        hostName: data.hostName || "Unknown",
        playerCount: data.playerCount || 0,
        maxPlayers: data.maxPlayers || 4,
        gameMode: data.gameMode || "standard",
        createdAt: data.createdAt?.toDate() || new Date()
      });
    });
    
    return games;
  } catch (error) {
    console.error("Error getting active games:", error);
    return [];
  }
}
// Add to AdminConsoleScene.js at line 424, after the getActiveGames function definition

// Quick access to create test game and join it
async function quickJoinGame(gameCode) {
  try {
    // Get current user
    const user = auth.currentUser;
    if (!user) {
      console.error("No user logged in");
      return;
    }
    
    await joinGame(gameCode, user.uid, user.displayName || "Admin");
    
    return gameCode;
  } catch (error) {
    console.error("Error joining game:", error);
    return null;
  }
}

export default AdminConsoleScene;